import { useMemo, useState } from 'react';
import { css } from '@emotion/react';
import {
  NoShowRateByType,
  RoiType,
} from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import { ChartCTA, DemoChip, InfoTipPopover } from '@frontend/analytics-shared';
import { BarChartData, Chart, YAxisLabelValueTick } from '@frontend/charts';
import { useTranslation } from '@frontend/i18n';
import { formatters } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { useFetchNSRROITimeline } from '../hooks/use-fetch-nsr-roi-timeline';
import { useNSRRoiShallowStore } from '../hooks/use-nsr-roi-store';

interface Props {
  highlightDescription?: (highlight: string) => string;
  infoText: string;
  isDemoAccount?: boolean;
  keysSequence?: string[];
  noShowRateType: NoShowRateByType.DAY_WISE | NoShowRateByType.APPOINTMENTS_EXTERNAL_TYPE;
  title: string;
}

export const NSRRoiGroupComparisonChart = ({
  highlightDescription,
  infoText,
  isDemoAccount,
  keysSequence,
  noShowRateType,
  title,
}: Props) => {
  const { t } = useTranslation('analytics');
  const { filters } = useNSRRoiShallowStore('filters');
  const [isLargeChart, setIsLargeChart] = useState<boolean>(false);

  const { aggregates = {}, isLoading } = useFetchNSRROITimeline({
    additionalRequestPayload: {
      roiType: RoiType.WEAVE,
      noShowRateType,
    },
    filters,
  });

  const { highlight, description, sortedAggregates } = useMemo(() => {
    const entries = keysSequence?.length
      ? // Sort by the order defined in keysSequence and add missing keys in between
        keysSequence
          .map((key) => [key, aggregates[key] || 0] as const)
          .concat(Object.entries(aggregates).filter(([key]) => !keysSequence.includes(key)))
      : // Sort by value (desc), then by key (alpha) for ties
        Object.entries(aggregates).sort((a, b) => (b[1] || 0) - (a[1] || 0) || a[0].localeCompare(b[0]));

    const highlightEntry = entries.length
      ? keysSequence?.length
        ? // Pick the item with the highest value from the list
          [...entries].sort((a, b) => Math.round(b[1] || 0) - Math.round(a[1] || 0))[0][0]
        : // Pick the first item from the sorted list
          entries[0][0]
      : t('NA');

    return {
      sortedAggregates: entries,
      highlight: highlightEntry === 'other' ? t('Other') : highlightEntry,
      description: entries.length
        ? highlightDescription?.(highlightEntry) ?? ''
        : t('No data available to determine no-show rates'),
    };
  }, [aggregates, highlightDescription, keysSequence, t]);

  const chartData = useMemo<BarChartData>(() => {
    return {
      groups:
        sortedAggregates.map(([key, value]) => {
          return {
            name: key,
            values: {
              value,
            },
          };
        }) || [],
    };
  }, [sortedAggregates]);

  return (
    <Chart
      colors={{ value: theme.colors.secondary.seaweed40 }}
      css={styles.chart}
      isLoading={isLoading}
      labels={{
        other: t('Other'),
        value: t('No-Show Rate'),
      }}
    >
      <Chart.Header
        title={title}
        infoTip={<InfoTipPopover>{infoText}</InfoTipPopover>}
        leftElement={isDemoAccount && <DemoChip />}
        removeHeaderBottomMargin
      />
      <Chart.BarChart
        appearance={{
          barSize: 32,
          customTooltipTitle: ({ groupName }) => {
            return groupName;
          },
          customYAxisTick: ({ labels, groupName, ...rest }) => {
            return (
              <YAxisLabelValueTick
                {...rest}
                label={labels?.[groupName] || groupName}
                onChartSizeChange={(isLargeChart) => setIsLargeChart(isLargeChart)}
              />
            );
          },
          groupsGap: 16,
          labelConfig: {
            customStyles: () => ({
              dy: 4,
              fontWeight: theme.font.weight.bold,
            }),
            placement: 'end',
            show: true,
          },
          layout: 'vertical',
          margin: { left: isLargeChart ? 90 : 40, right: 50 },
          maxHeight: 340,
          showYAxis: true,
        }}
        data={chartData}
        formatValue={formatters.appendPercent}
      />
      <ChartCTA
        buttonLabel={t('Send Reminder')}
        ctaType='sendReminders'
        title={t('Highest No-Show')}
        highlight={highlight}
        description={description}
      />
    </Chart>
  );
};

const styles = {
  chart: css`
    .chart-content {
      justify-content: space-around;
    }
  `,
};
